import { recipeById, type CraftRecipe } from "../content/recipes";
import type { WeaponId } from "../content/schemas";
import { WEAPONS } from "../content/weapons";
import type { BankedProfile } from "./persistence";

export type CraftResult =
  | { ok: true; recipe: CraftRecipe; profile: BankedProfile; message: string }
  | { ok: false; message: string };

export function isRecipeOwned(profile: BankedProfile, recipe: CraftRecipe): boolean {
  if (recipe.unlocksFrame && profile.unlockedFrames.includes(recipe.unlocksFrame)) return true;
  if (recipe.unlocksWeapon && profile.unlockedWeapons.includes(recipe.unlocksWeapon)) return true;
  if (recipe.unlocksCosmetic && profile.cosmetics.includes(recipe.unlocksCosmetic)) return true;
  if (recipe.grantsBlueprint && profile.blueprints.includes(recipe.grantsBlueprint)) return true;
  return false;
}

export function canCraft(
  profile: BankedProfile,
  recipe: CraftRecipe,
): { ok: true } | { ok: false; message: string } {
  if (isRecipeOwned(profile, recipe)) return { ok: false, message: "Already owned." };
  if (recipe.requiresRank && profile.pilotMeta.rank < recipe.requiresRank) {
    return { ok: false, message: `Requires Pilot Rank ${recipe.requiresRank}.` };
  }
  if (profile.salvage < recipe.salvageCost) {
    return {
      ok: false,
      message: `Need ${recipe.salvageCost} banked salvage (have ${profile.salvage}).`,
    };
  }
  if (recipe.requiresBlueprint && !profile.blueprints.includes(recipe.requiresBlueprint)) {
    return { ok: false, message: `Requires blueprint: ${recipe.requiresBlueprint}` };
  }
  return { ok: true };
}

export function applyCraft(profile: BankedProfile, recipeId: string): CraftResult {
  const recipe = recipeById(recipeId);
  if (!recipe) return { ok: false, message: "Unknown recipe." };
  const gate = canCraft(profile, recipe);
  if (!gate.ok) return { ok: false, message: gate.message };

  let next: BankedProfile = {
    ...profile,
    salvage: profile.salvage - recipe.salvageCost,
    unlockedFrames: [...profile.unlockedFrames],
    unlockedWeapons: [...profile.unlockedWeapons],
    cosmetics: [...profile.cosmetics],
    blueprints: [...profile.blueprints],
    selectedWeapons: [...profile.selectedWeapons],
    settings: { ...profile.settings },
  };

  if (recipe.unlocksFrame && !next.unlockedFrames.includes(recipe.unlocksFrame)) {
    next.unlockedFrames.push(recipe.unlocksFrame);
  }
  if (recipe.unlocksWeapon && !next.unlockedWeapons.includes(recipe.unlocksWeapon)) {
    next.unlockedWeapons.push(recipe.unlocksWeapon);
    // Auto-equip into that slot so craft has an immediate combat effect.
    next = equipWeaponInSlot(next, recipe.unlocksWeapon);
  }
  if (recipe.unlocksCosmetic && !next.cosmetics.includes(recipe.unlocksCosmetic)) {
    next.cosmetics.push(recipe.unlocksCosmetic);
  }
  if (recipe.grantsBlueprint && !next.blueprints.includes(recipe.grantsBlueprint)) {
    next.blueprints.push(recipe.grantsBlueprint);
  }

  const equipNote =
    recipe.unlocksWeapon != null
      ? ` Equipped in ${WEAPONS[recipe.unlocksWeapon]?.slot ?? "loadout"}.`
      : "";

  return {
    ok: true,
    recipe,
    profile: next,
    message: `Crafted ${recipe.name}.${equipNote}`,
  };
}

/** Equip a weapon into its slot (replaces any other weapon in that slot). */
export function equipWeaponInSlot(profile: BankedProfile, weaponId: WeaponId): BankedProfile {
  const def = WEAPONS[weaponId];
  if (!def || !profile.unlockedWeapons.includes(weaponId)) return profile;
  const kept = profile.selectedWeapons.filter((id) => WEAPONS[id]?.slot !== def.slot);
  return { ...profile, selectedWeapons: [...kept, weaponId] };
}

/**
 * Toggle / swap loadout by slot: one primary, one secondary, one ordnance max.
 * Clicking the equipped weapon unequips it (unless it would empty the loadout).
 */
export function toggleSelectedWeapon(profile: BankedProfile, weaponId: WeaponId): BankedProfile {
  if (!profile.unlockedWeapons.includes(weaponId)) return profile;
  const def = WEAPONS[weaponId];
  if (!def) return profile;

  if (profile.selectedWeapons.includes(weaponId)) {
    if (profile.selectedWeapons.length <= 1) return profile;
    return {
      ...profile,
      selectedWeapons: profile.selectedWeapons.filter((id) => id !== weaponId),
    };
  }
  return equipWeaponInSlot(profile, weaponId);
}
